import { z } from 'zod'

import {
  organizationId,
  type OrganizationId,
} from '../schemas/public/Organization.js'
import type {
  Project,
  ProjectInitializer,
  ProjectMutator,
} from '../schemas/public/Project.js'
import {
  project,
  projectInitializer,
  projectMutator,
} from '../schemas/public/Project.js'
import type { EmbeddedLoggerLastTransmission } from './LoggerWithContext.js'

export type ProjectWithOrgInitializer = ProjectInitializer & {
  organizationId: OrganizationId
}

export type ProjectWithOrgMutator = ProjectMutator & {
  organizationId?: OrganizationId
}

export type ProjectExtras = {
  lastLogged?: string
  loggerCount?: number
  photos?: Array<{
    albumAddDate: number
    height: number
    imageUpdateDate: number
    uid: string
    url: string
    width: number
  }>
  geo?: Array<{
    id: number
    name: string
    status: string
    visible: boolean
    latitude: number
    longitude: number
  }>
  changelog?: any
}

export type ProjectWithOrganization = Project & {
  organizationId: OrganizationId
  organizationName: string
  siteCount?: number | null
  activeSiteCount?: number | null
  retiredSiteCount?: number | null
  cableCount?: number | null
  extras?: ProjectExtras
  lastTransmission?: EmbeddedLoggerLastTransmission
}

export const orgFields = z.object({
  organizationId: z.number().int().positive(),
})

export const projectSlug = z.object({
  slug: z
    .preprocess(
      (v) => typeof v === 'string' && v.trim().toLowerCase().replace(/_/g, '-'),
      z.string().regex(/^[a-z0-9]([-_]?[a-z0-9])*$/),
    )
    .nullable()
    .optional(),
})

export const projectWithOrgInitializer = z.object({
  organizationId: organizationId,
  ...(projectInitializer as unknown as z.ZodObject<any>).shape,
  ...projectSlug.shape,
}) as unknown as z.Schema<ProjectWithOrgInitializer>

export const projectWithOrgMutator = z.object({
  organizationId: organizationId.optional(),
  ...(projectMutator as unknown as z.ZodObject<any>).shape,
  ...projectSlug.shape,
}) as unknown as z.Schema<ProjectWithOrgMutator>

export const projectWithOrg = z.object({
  ...orgFields.shape,
  ...(project as unknown as z.ZodObject<any>).shape,
  ...projectSlug.shape,
}) as unknown as z.Schema<ProjectWithOrganization>
